import React, { useCallback, useId, useState } from 'react';
import './ClientMedicaidSection.css';

const CLIENT_MEDICAID_HELP_TEXT =
  'Stored on the client record (not on the referral). You can indicate Medicaid before the 9-digit RIN is available.';

function ClientMedicaidInfoMark() {
  return (
    <svg
      className="client-medicaid-info-svg"
      viewBox="0 0 20 20"
      width="20"
      height="20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.35" />
      <circle cx="10" cy="7" r="1.15" fill="currentColor" />
      <rect x="9" y="9.6" width="2" height="5.2" rx="1" fill="currentColor" />
    </svg>
  );
}

/**
 * Client-level Medicaid (not referral coverage). Shown on the client form and mirrored on the referral insurance workspace.
 */
function ClientMedicaidSection({ hasMedicaid, medicaidRin, onMedicaidFieldChange, disabled, permissionHint, className = '' }) {
  const helpId = useId();
  const [helpOpen, setHelpOpen] = useState(false);

  const handleMedicaidRinChange = useCallback(
    (e) => {
      if (disabled) return;
      const next = String(e.target.value).replace(/\D/g, '').slice(0, 9);
      const prevDigits = String(medicaidRin || '').replace(/\D/g, '');

      if (prevDigits.length > 0 && next.length === 0 && hasMedicaid) {
        const ok = window.confirm(
          'You removed the Medicaid RIN. "Client has Medicaid" will be unchecked so the record stays consistent. Continue?'
        );
        if (!ok) return;
        onMedicaidFieldChange({ name: 'medicaid_rin', value: '' });
        onMedicaidFieldChange({ name: 'has_medicaid', value: false });
        return;
      }

      onMedicaidFieldChange({ name: 'medicaid_rin', value: next });
      if (next.length === 9 && !hasMedicaid) {
        onMedicaidFieldChange({ name: 'has_medicaid', value: true });
      }
    },
    [disabled, hasMedicaid, medicaidRin, onMedicaidFieldChange]
  );

  return (
    <div className={`form-section client-medicaid-section${className ? ` ${className}` : ''}`}>
      <h3 className="section-title client-medicaid-section-heading">
        <span>Client </span>
        <span className="client-medicaid-section-heading-medicaid">
          Medicaid
          <button
            type="button"
            className="client-medicaid-info-btn"
            onClick={() => setHelpOpen((v) => !v)}
            aria-expanded={helpOpen}
            aria-controls={helpId}
            aria-label={helpOpen ? 'Hide Client Medicaid help' : 'Show Client Medicaid help'}
          >
            <ClientMedicaidInfoMark />
          </button>
        </span>
      </h3>
      {helpOpen ? (
        <p id={helpId} className="client-medicaid-help-text">
          {CLIENT_MEDICAID_HELP_TEXT}
        </p>
      ) : null}
      {permissionHint ? (
        <p className="form-group-hint form-group-hint-below-label" role="status">
          {permissionHint}
        </p>
      ) : null}
      <div className="form-grid client-medicaid-section-grid">
        <div className="form-group client-medicaid-checkbox-group">
          <label className="referral-form-checkbox-label">
            <input
              type="checkbox"
              checked={!!hasMedicaid}
              onChange={(e) => onMedicaidFieldChange({ name: 'has_medicaid', value: e.target.checked })}
              disabled={disabled}
            />
            <span>Client has Medicaid</span>
          </label>
        </div>
        <div className="form-group">
          <label htmlFor="client-medicaid-rin">Medicaid RIN</label>
          <input
            id="client-medicaid-rin"
            type="text"
            name="medicaid_rin"
            inputMode="numeric"
            autoComplete="off"
            value={medicaidRin || ''}
            onChange={handleMedicaidRinChange}
            maxLength={9}
            placeholder="9 digits, optional"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

export default ClientMedicaidSection;
